import FormatAlignCenter from '@mui/icons-material/FormatAlignCenter'
import FormatAlignJustify from '@mui/icons-material/FormatAlignJustify'
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft'
import FormatAlignRight from '@mui/icons-material/FormatAlignRight'
import FormatBold from '@mui/icons-material/FormatBold'
import FormatItalic from '@mui/icons-material/FormatItalic'
import FormatUnderlined from '@mui/icons-material/FormatUnderlined'
import { Box, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { CustomInput } from 'src/consumer/components/helperComponents'
import ColorSwatchInput from './ColorSwatchInput'

/**
 * Reusable component for editing TemplateTextProperties
 * @param {object} data - The TemplateTextProperties object
 * @param {function} setData - Setter function that accepts event objects
 * @param {string} label - Label for this text properties editor
 * @param {boolean} disabled - Whether inputs are disabled
 */
export default function TextPropertiesEditor({ data = {}, setData, label, disabled = false }) {
  const createEvent = (id, value) => ({
    target: {
      id,
      value: typeof value === 'boolean' ? null : value,
      checked: typeof value === 'boolean' ? value : null,
      type: typeof value === 'boolean' ? 'checkbox' : 'text',
    },
  })

  const handleFormatChange = (event, newFormats) => {
    if (!Array.isArray(newFormats)) return

    // Batch all 3 format updates into a single state update
    setData((prevData) => ({
      ...prevData,
      textBold: newFormats.includes('bold'),
      textItalic: newFormats.includes('italic'),
      textUnderline: newFormats.includes('underline'),
    }))
  }

  const handleAlignChange = (event, newAlign) => {
    if (newAlign !== null) {
      setData(createEvent('textAlign', newAlign))
    }
  }

  const handleColorChange = (e) => {
    setData(createEvent('textColor', e.target.value))
  }

  const handleFontPlusChange = (e) => {
    setData(createEvent('textFontPlus', parseInt(e.target.value) || 0))
  }

  // Build current format array for ToggleButtonGroup
  const currentFormats = []
  if (data?.textBold) currentFormats.push('bold')
  if (data?.textItalic) currentFormats.push('italic')
  if (data?.textUnderline) currentFormats.push('underline')

  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      )}

      <Grid container spacing={1}>
        {/* Text Formatting (Bold, Italic, Underline) */}
        <Grid item xs={12}>
          <div className="d-flex align-items-center gap-2">
            {/* <Typography variant="caption" color="textSecondary">
              Text Formatting
            </Typography> */}
            <ToggleButtonGroup
              size="small"
              value={currentFormats}
              onChange={handleFormatChange}
              disabled={disabled}
              color="primary"
              sx={{ mt: 0.5 }}
            >
              <ToggleButton value="bold" aria-label="bold">
                <FormatBold fontSize="small" />
              </ToggleButton>
              <ToggleButton value="italic" aria-label="italic">
                <FormatItalic fontSize="small" />
              </ToggleButton>
              <ToggleButton value="underline" aria-label="underline">
                <FormatUnderlined fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </Grid>

        {/* Text Alignment */}
        <Grid item xs={12}>
          <div className="d-flex align-items-center gap-2">
            {/* <Typography variant="caption" color="textSecondary">
              Text Alignment
            </Typography> */}
            <ToggleButtonGroup
              size="small"
              value={data?.textAlign || 'left'}
              color="primary"
              exclusive
              onChange={handleAlignChange}
              disabled={disabled}
              sx={{ mt: 0.5 }}
            >
              <ToggleButton value="left" aria-label="left aligned">
                <FormatAlignLeft fontSize="small" />
              </ToggleButton>
              <ToggleButton value="center" aria-label="centered">
                <FormatAlignCenter fontSize="small" />
              </ToggleButton>
              <ToggleButton value="right" aria-label="right aligned">
                <FormatAlignRight fontSize="small" />
              </ToggleButton>
              <ToggleButton value="justify" aria-label="justified">
                <FormatAlignJustify fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </Grid>

        {/* Text Color — compact circle picker (matches the section style editor) */}
        <Grid item xs={12} sx={{ mt: 0.5 }}>
          <ColorSwatchInput
            label="Text Color"
            value={data?.textColor || '#000000'}
            onChange={handleColorChange}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12}>
          <CustomInput
            label="Font Size Adjust"
            type="number"
            value={data?.textFontPlus ?? 0}
            onChange={handleFontPlusChange}
            disabled={disabled}
            inputProps={{ min: -5, max: 10, step: 1 }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
